import { useCallback, useRef, useState } from "react";
import { type EntityFileUploadCategory } from "@/react/components/entityViews/files/constants/entityFileUpload";

export interface UseFileDragAndDropParams {
  /** Call-site documents which widget; filtering lives in uploadFiles / useEntityFileUpload */
  category: EntityFileUploadCategory;
  uploadFiles: (files: FileList | File[]) => Promise<void>;
  enabled?: boolean;
}

export interface UseFileDragAndDropResult {
  isDraggingOver: boolean;
  handleDragEnter: (event: React.DragEvent) => void;
  handleDragLeave: (event: React.DragEvent) => void;
  handleDragOver: (event: React.DragEvent) => void;
  handleDrop: (event: React.DragEvent) => void;
}

/** Nested enter/leave counter so child hover does not flicker the drop highlight. */
export function useFileDragAndDrop({
  uploadFiles,
  enabled = true,
}: UseFileDragAndDropParams): UseFileDragAndDropResult {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dragCounterRef = useRef(0);

  const handleDragEnter = useCallback(
    (event: React.DragEvent) => {
      if (!enabled) return;

      event.preventDefault();
      event.stopPropagation();

      dragCounterRef.current += 1;
      if (dragCounterRef.current === 1) setIsDraggingOver(true);
    },
    [enabled],
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent) => {
      if (!enabled) return;

      event.preventDefault();
      event.stopPropagation();

      dragCounterRef.current -= 1;
      if (dragCounterRef.current <= 0) {
        dragCounterRef.current = 0;
        setIsDraggingOver(false);
      }
    },
    [enabled],
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent) => {
      if (!enabled) return;

      event.preventDefault();
      event.stopPropagation();

      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "copy";
      }
    },
    [enabled],
  );

  const handleDrop = useCallback(
    async (event: React.DragEvent) => {
      if (!enabled) return;

      event.preventDefault();
      event.stopPropagation();

      setIsDraggingOver(false);
      dragCounterRef.current = 0;

      const files = event.dataTransfer?.files;
      if (!files || files.length === 0) return;

      await uploadFiles(files);
    },
    [enabled, uploadFiles],
  );

  return {
    isDraggingOver,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
  };
}
